/**
 * The advanced-search help (#2290), from the `?` in the box and from an
 * empty result: every filter with an example and what it does, how filters
 * combine and how to exclude, and complete example searches. Picking an
 * example puts it in the box.
 */
import Modal from '@/components/common/Modal';
import { FILTER_HELP, HELP_EXAMPLES } from './searchHelpContent';
import './discoverSearch.css';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Put an example in the box. */
  onExample: (query: string) => void;
}

export default function SearchHelp({ open, onClose, onExample }: Readonly<Props>) {
  return (
    <Modal open={open} onClose={onClose} title="Search help" size="lg">
      <div className="dsq-help">
        <p className="mb-3">
          Type words to search names, descriptions and tags. Add filters in the box, or use{' '}
          <strong>Filters</strong>: both make the same search.
        </p>

        <h3>Filters</h3>
        <dl className="dsq-help-list">
          {FILTER_HELP.map((f) => (
            <div key={f.example} className="contents">
              <dt><code className={`dsq-k-${f.kind}`}>{f.example}</code></dt>
              <dd>{f.what}</dd>
            </div>
          ))}
        </dl>

        <h3>Combining and excluding</h3>
        <ul>
          <li>Filters and words separated by spaces must <em>all</em> match.</li>
          <li>Several <code className="dsq-k-author">author:</code> filters match <em>any</em> of the people; <code className="dsq-k-tag">a|b</code> matches either tag.</li>
          <li>Put <code className="dsq-k-exclude">-</code> before <code className="dsq-k-tag">tag:</code> to exclude a tag.</li>
          <li>Quote values with spaces: <code className="dsq-k-uses">uses:"Geo Buffer"</code>.</li>
          <li>A filter applies, and shows as a chip, once you type a space after it or press Enter. Words with an unknown prefix, like <code>note:berth</code>, are searched as text.</li>
        </ul>

        <h3>Examples</h3>
        <div className="dsq-examples">
          {HELP_EXAMPLES.map((ex) => (
            <button
              key={ex.query}
              type="button"
              className="dsq-example"
              onClick={() => onExample(ex.query)}
              aria-label={`Search for ${ex.query}`}
            >
              <span className="dsq-example-q">{ex.query}</span>
              <span className="dsq-example-d">{ex.what}</span>
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
